import React, { useRef, useEffect } from "react"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
} from "chart.js"

// Register the pieces we actually use (legend intentionally not registered)
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
)

type SeriesPoint = { date: string | Date; irr: number | null }

function formatQuarterLabel(d: Date) {
  const q = Math.floor(d.getMonth() / 3) + 1
  return `Q${q} ${d.getFullYear()}`
}

function toDate(value: string | Date) {
  return value instanceof Date ? value : new Date(value)
}

interface ChartData {
  series?: SeriesPoint[]
}

const LpHighLevelKpisAndHighlightsChart = ({
  data = {},
}: {
  data?: ChartData
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const chartRef = useRef<ChartJS | null>(null)

  // --- map API series -> labels + values ---
  const series: SeriesPoint[] = (
    Array.isArray(data.series) ? data.series : []
  ).slice(-12)

  const labels = series.map((p) => formatQuarterLabel(toDate(p.date)))
  const values = series.map((p) => (p.irr == null ? null : Number(p.irr)))

  // colors: last point green, others navy
  const navy = "#1f4788"
  const green = "#5fa97b"
  const lastIndex = Math.max(0, values.length - 1)
  const pointColors = values.map((_, i) => (i === lastIndex ? green : navy))

  // --- compute dynamic y-axis bounds from actual data ---
  const validValues = values.filter(
    (v): v is number => v !== null && isFinite(v),
  )
  const dataMin = validValues.length > 0 ? Math.min(...validValues) : 0
  const dataMax = validValues.length > 0 ? Math.max(...validValues) : 16
  const range = dataMax - dataMin || 1
  // 30% headroom above max for value labels; 20% below min (floor at 0 unless data is negative)
  const yMin = Math.floor(Math.min(0, dataMin - range * 0.2))
  const yMax = Math.ceil(dataMax + range * 0.3)

  // --- build chart config from real data (no hardcoded defaults) ---
  const chartConfig = {
    type: "line" as const,
    data: {
      labels,
      datasets: [
        {
          data: values,
          borderColor: "transparent", // custom segments drawn via plugin
          pointBackgroundColor: pointColors,
          pointBorderColor: pointColors,
          pointRadius: 6,
          borderWidth: 3,
          tension: 0.1,
          spanGaps: true, // allow nulls to create gaps
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        intersect: false,
        mode: null as any, // fully disable interactions
      },
      hover: {
        mode: null as any,
        intersect: false,
        animationDuration: 0 as any,
      },
      onHover: null as any,
      scales: {
        y: {
          beginAtZero: false,
          min: yMin,
          max: yMax,
          display: false,
          grid: { display: false },
          border: { display: false },
        },
        x: {
          display: true,
          grid: {
            display: false,
            drawTicks: false,
            drawBorder: false,
          },
          border: {
            display: false,
            width: 0,
          },
          ticks: {
            color: "#1f4788",
            font: { weight: "bold" as const, size: 16 },
            drawTicks: false as any,
          },
        },
      },
      animation: {
        duration: 1000,
      },
      plugins: {
        legend: { display: false },
        title: { display: false },
        tooltip: { enabled: false },
      },
    },
    // Custom plugins (unchanged, but now driven by real data)
    plugins: [
      {
        // Draw custom colored line segments (last segment green)
        beforeDatasetsDraw: function (chart: ChartJS) {
          const ctx = chart.ctx
          const meta = chart.getDatasetMeta(0)

          if (meta && meta.data.length > 1) {
            ctx.save()
            ctx.lineWidth = 3

            for (let i = 0; i < meta.data.length - 1; i++) {
              const currentPoint = meta.data[i]
              const nextPoint = meta.data[i + 1]
              ctx.strokeStyle = i === meta.data.length - 2 ? green : navy

              ctx.beginPath()
              ctx.moveTo((currentPoint as any).x, (currentPoint as any).y)
              ctx.lineTo((nextPoint as any).x, (nextPoint as any).y)
              ctx.stroke()
            }

            ctx.restore()
          }
        },
      },
      {
        // Draw custom vertical grid lines between data points
        beforeDraw: function (chart: ChartJS) {
          const ctx = chart.ctx
          const chartArea = chart.chartArea
          const meta = chart.getDatasetMeta(0)

          if (meta && meta.data.length > 0) {
            ctx.save()
            ctx.strokeStyle = "#e5e7eb"
            ctx.lineWidth = 1
            ctx.setLineDash([])

            for (let i = 0; i < meta.data.length - 1; i++) {
              const currentPoint = meta.data[i]
              const nextPoint = meta.data[i + 1]
              const midX = ((currentPoint as any).x + (nextPoint as any).x) / 2

              ctx.beginPath()
              ctx.moveTo(midX, chartArea.top)
              ctx.lineTo(midX, chartArea.bottom)
              ctx.stroke()
            }

            // Edge lines
            const firstPoint = meta.data[0] as any
            const lastPoint = meta.data[meta.data.length - 1] as any
            const spacing =
              meta.data.length > 1
                ? (meta.data[1] as any).x - (firstPoint as any).x
                : 40 // arbitrary fallback

            const firstX = firstPoint.x - spacing / 2
            const lastX = lastPoint.x + spacing / 2

            ctx.beginPath()
            ctx.moveTo(firstX, chartArea.top)
            ctx.lineTo(firstX, chartArea.bottom)
            ctx.stroke()

            ctx.beginPath()
            ctx.moveTo(lastX, chartArea.top)
            ctx.lineTo(lastX, chartArea.bottom)
            ctx.stroke()

            ctx.restore()
          }
        },
      },
      {
        // Draw value labels above each point
        afterDatasetsDraw: function (chart: ChartJS) {
          const ctx = chart.ctx
          const dataset = chart.data.datasets[0] as any
          const meta = chart.getDatasetMeta(0)

          if (meta?.hidden) return
          const dsData: (number | null)[] = dataset.data

          // Process right-to-left so the rightmost (current quarter) label is
          // always drawn. Track bounding boxes and skip any label that would
          // overlap one already drawn.
          const drawnBoxes: Array<{
            left: number
            right: number
            top: number
            bottom: number
          }> = []

          const elements = meta.data as any[]
          ;[...elements].reverse().forEach((element, reversedIndex) => {
            const index = elements.length - 1 - reversedIndex
            const v = dsData[index]
            if (typeof v !== "number" || !isFinite(v)) return

            const colorArray = dataset.pointBackgroundColor as string[] | string
            const color = Array.isArray(colorArray)
              ? colorArray[index]
              : colorArray || navy

            ctx.save()
            ctx.font = "bold 14px Arial"
            ctx.textBaseline = "bottom"

            const label = `${v.toFixed(1)}%`
            const halfWidth = ctx.measureText(label).width / 2
            const labelHeight = 14
            const chartRight = chart.chartArea.right
            const chartLeft = chart.chartArea.left
            // Clamp x so the label never overflows either edge of the chart area
            const x = Math.min(
              Math.max(element.x, chartLeft + halfWidth),
              chartRight - halfWidth,
            )
            const top = element.y - 10 - labelHeight
            const bottom = element.y - 10
            const left = x - halfWidth - 2
            const right = x + halfWidth + 2

            const overlaps = drawnBoxes.some(
              (b) =>
                left < b.right &&
                right > b.left &&
                top < b.bottom &&
                bottom > b.top,
            )

            if (!overlaps) {
              drawnBoxes.push({ left, right, top, bottom })
              ctx.fillStyle = color
              ctx.textAlign = "center"
              ctx.fillText(label, x, element.y - 10)
            }

            ctx.restore()
          })
        },
      },
    ],
  }

  useEffect(() => {
    const el = canvasRef.current
    if (!el) return

    // Destroy existing chart if it exists
    if (chartRef.current) {
      chartRef.current.destroy()
    }

    // Set higher device pixel ratio for crisp PDF rendering
    // This ensures canvas renders at 2x resolution for print quality
    const dpr = Math.max(window.devicePixelRatio || 1, 2)
    ChartJS.defaults.devicePixelRatio = dpr

    // Create new chart from real data config
    chartRef.current = new ChartJS(el, {
      type: chartConfig.type,
      data: chartConfig.data,
      options: chartConfig.options as any,
      plugins: chartConfig.plugins as any,
    })

    return () => {
      chartRef.current?.destroy()
      chartRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(labels), JSON.stringify(values)])

  return (
    <div
      style={{
        backgroundColor: "transparent",
        height: "400px",
        position: "relative",
      }}
    >
      <canvas ref={canvasRef} />
    </div>
  )
}

export default LpHighLevelKpisAndHighlightsChart
